import { createSelector } from 'reselect';

import { getAuthenticationState } from './selector';
import { getUserPreference } from 'src/module/user/preferences';
import { getIsOnPremDeployment } from 'src/utils/deployment';

/*
 * The switch to the redesigned Chemelex UI (ADR-00003 SD2):
 *
 *   on new UI  <=>  features includes 'chemelex-ui'  AND  preference !== false
 *
 * Features are granted per-facility, so the feature grants eligibility and the
 * preference can only ever take an individual user OFF.
 */

/** Registered by migrations/20260805000001_add_chemelex_ui_feature.ts. */
export const CHEMELEX_UI_FEATURE = 'chemelex-ui';


export const CHEMELEX_UI_ALPHA_FEATURE = 'chemelex-ui-alpha';

const hasFeature = (key: string) => createSelector(
  [getAuthenticationState],
  (authentication) => {
    // Either a plain array or a List depending on the code path; both have
    // .includes, as hasFeaturePermissions in ./selector also assumes.
    const features = authentication.get('features', []);
    return features.includes(key);
  }
);

const isChemelexUiGranted = hasFeature(CHEMELEX_UI_FEATURE);

/* v2 is cloud-only: on-prem stays on the classic UI whatever its tier grants. */
export const hasChemelexUiFeature = (state) => isChemelexUiGranted(state) && !getIsOnPremDeployment();

export const hasChemelexUiAlphaFeature = hasFeature(CHEMELEX_UI_ALPHA_FEATURE);

export const getChemelexUiBannerDismissed = createSelector(
  [getAuthenticationState],
  (authentication) => {
    return getUserPreference(authentication, 'chemelexUiBannerDismissed') === true;
  }
);

/**
 * The `true` default is load-bearing: defaulting to `false` would opt every user
 * out, silently disabling the rollout.
 */
export const getUseChemelexUiPreference = createSelector(
  [getAuthenticationState],
  (authentication) => {
    return getUserPreference(authentication, 'useChemelexUi') ?? true;
  }
);

/** `!== false` so absent and null both mean "on"; only explicit false opts out. */
export const shouldUseChemelexUi = createSelector(
  [hasChemelexUiFeature, getUseChemelexUiPreference],
  (hasFeature, preference) => {
    return hasFeature && preference !== false;
  }
);
